// 空港データ + 着陸/接近判定ヘルパ。
//
// AIRPORTS = キュレーション148件（airportsCurated.js, 日本語名の主要空港）
//          + 世界の空港（airportsData.js, OurAirports 由来 約5,000件）。
// 世界データは tools/compile-airports で生成（OurAirports CSV → 絞込→滑走路方位
// 結合→Wikidata/keywords 日本語名→重複除去）。IATA/ICAO でキュレーションと重複
// する空港は生成側で除外済み。
//
// 各空港: { name, iata?, icao?, lat, lng, elevationM, runwayHeadingsDeg:[真方位...],
//           captureRadiusM, size?, nameEn?, serves? }
//  - runwayHeadingsDeg: 滑走路方向の「真方位」(度)。両方向を列挙（判定側で reciprocal 許容）。
//  - captureRadiusM: 空港敷地を覆う捕捉半径(m)。ここに接地し方位が合えば「着陸」。
//  - size: 'large' | 'medium'（キュレーションは undefined）。接近トースト抑制に使う。
// ヘルパ関数(下部)はデータ長・内容に非依存。
import { CURATED_AIRPORTS } from './airportsCurated.js';
import { WORLD_AIRPORTS } from './airportsData.js';
import { angleDiff, nearestPoi } from './geo.js';

export const AIRPORTS = [...CURATED_AIRPORTS, ...WORLD_AIRPORTS];

const DEG2RAD = Math.PI / 180;
const ALIGN_TOL_RAD = 20 * DEG2RAD; // 滑走路方位との許容ずれ（±20°）

// 各空港の ENU 座標(+X=西, +Z=北)を前計算。startGame で一度だけ呼ぶ。
// enu は geo.js createEnuFrame() の戻り値。楕円体正確な変換なので、原点から
// 数百 km 離れた空港でも機体の ENU 位置と整合し、captureRadiusM の着陸判定が
// 遠方でも成立する（旧・平面近似は東京→関西で約10kmずれて判定不能だった）。
export function airportsToEnu(enu) {
  return AIRPORTS.map((ap) => {
    const p = enu.latLngToWorld(ap.lat, ap.lng, ap.elevationM);
    return {
      ...ap,
      x: p.x,
      y: p.y, // nearestPoi の 3D 順位付けに必要（対蹠の折り返し防止）
      z: p.z,
      headingsRad: ap.runwayHeadingsDeg.map((d) => d * DEG2RAD),
    };
  });
}

// 最寄り空港 + 水平距離(m) + 機体→空港の方位(rad, 0=北,+東)。
// 走査そのものは geo.js の nearestPoi（3D 弦距離で順位付け）に任せる。
export function nearestAirport(enuAirports, x, y, z) {
  const near = nearestPoi(enuAirports, x, y, z);
  return near && { airport: near.item, distanceM: near.distanceM, bearingRad: near.bearingRad };
}

// 着陸判定: (x,z) が空港の捕捉円内かつ heading が滑走路方位に整合する空港を返す（無ければ null）。
export function landingAirport(enuAirports, x, z, headingRad) {
  for (const ap of enuAirports) {
    const d = Math.hypot(ap.x - x, ap.z - z);
    if (d > ap.captureRadiusM) continue;
    if (isAligned(headingRad, ap.headingsRad)) return ap;
  }
  return null;
}

function isAligned(headingRad, runwayHeadingsRad) {
  for (const rw of runwayHeadingsRad) {
    // 滑走路は両方向から進入できるので reciprocal も許容。
    if (Math.abs(angleDiff(headingRad, rw)) <= ALIGN_TOL_RAD) return true;
    if (Math.abs(angleDiff(headingRad, rw + Math.PI)) <= ALIGN_TOL_RAD) return true;
  }
  return false;
}
